/**
 * Die Töne der App: Klingeln bei einem Anruf, Alarm bei SOS.
 *
 * ## Warum das eine eigene Datei ist und nicht zwei
 *
 * Beide teilen sich das Schwierige daran — den Browser dazu zu bringen,
 * überhaupt einen Ton zu erlauben. Zweimal gelöst hiesse zweimal falsch
 * gelöst.
 *
 * ## Der Fehler, der hier vorher steckte
 *
 * Die erste Fassung legte bei **jedem** Klingeln einen neuen `AudioContext`
 * an. Zwei Dinge gehen dabei schief, und beide enden in Stille:
 *
 *  1. Ein Kontext, der ausserhalb einer Nutzerhandlung entsteht, startet
 *     angehalten. `resume()` darf der Browser dann verweigern — und tut es auf
 *     dem Telefon auch.
 *  2. Browser begrenzen die Zahl der Kontexte (Chrome: sechs). Ab dem siebten
 *     Anruf wirft `new AudioContext()`, der `catch` schluckt es, und es
 *     klingelt nie wieder. Ein Fehler, der erst nach einer Woche Benutzung
 *     auftritt und dann nicht mehr weggeht.
 *
 * ## Wie es jetzt läuft
 *
 * **Ein** Kontext für die ganze Sitzung, angelegt bei der **ersten Berührung**
 * der App — irgendein Tipp, irgendwo. Genau das ist die Nutzerhandlung, die
 * der Browser sehen will, und sie gilt danach für die ganze Seite („sticky
 * activation"). Ab dann ist Klingeln nur noch Rechnen.
 *
 * Beim Freischalten läuft ein stummer Puffer durch. Das klingt nach Aberglaube
 * und ist keiner: iOS gibt den Kontext erst frei, wenn wirklich etwas
 * abgespielt wurde.
 *
 * ## Was bleibt
 *
 * Wer die App seit dem Laden **nie** berührt hat, hört nichts — da hilft kein
 * Code, das ist die Regel des Browsers. In der Praxis kommt das kaum vor: Wer
 * die App benutzt, hat den Schlüsselspeicher entsperrt oder einen Reiter
 * getippt. Für den Rest gibt es die Systemmeldung (siehe `meldung.ts`) und
 * das Vibrieren.
 */

let ctx: AudioContext | null = null
let scharf = false

/** Was gerade läuft — damit `aus()` es auch wirklich beendet. */
let takt: ReturnType<typeof setInterval> | null = null
let laufendeToene: OscillatorNode[] = []

function neuerKontext(): AudioContext | null {
  try {
    const Ctx = window.AudioContext
      ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    return Ctx ? new Ctx() : null
  } catch {
    return null
  }
}

/**
 * Schaltet den Ton bei der ersten Berührung frei.
 *
 * Wird einmal aus `App.tsx` gerufen. Die Zuhörer entfernen sich selbst, sobald
 * es geklappt hat — es geht nur um den einen Moment.
 */
export function tonScharfmachen(): void {
  if (scharf) return
  scharf = true

  const freischalten = () => {
    if (!ctx) ctx = neuerKontext()
    if (!ctx) return
    void ctx.resume().catch(() => undefined)
    try {
      // Ein Sample Stille. iOS gibt den Kontext erst nach echter Ausgabe frei.
      const puffer = ctx.createBuffer(1, 1, 22_050)
      const quelle = ctx.createBufferSource()
      quelle.buffer = puffer
      quelle.connect(ctx.destination)
      quelle.start(0)
    } catch {
      // Dann eben ohne — `resume()` allein reicht auf den meisten Geräten.
    }
    for (const art of ['pointerdown', 'keydown', 'touchend'] as const) {
      removeEventListener(art, freischalten)
    }
  }

  for (const art of ['pointerdown', 'keydown', 'touchend'] as const) {
    addEventListener(art, freischalten, { passive: true })
  }

  /*
   * Kommt die App aus dem Hintergrund zurück, hält der Browser den Kontext oft
   * an. Ohne dieses Aufwecken bliebe der nächste Anruf stumm — und zwar genau
   * nach dem Fall, in dem das Telefon in der Tasche war.
   */
  addEventListener('visibilitychange', () => {
    if (!document.hidden && ctx?.state === 'suspended') {
      void ctx.resume().catch(() => undefined)
    }
  })
}

/** Ein Ton mit weichen Flanken — hart geschaltet knackt es. */
function ton(hz: number, ab: number, dauer: number, laut: number): void {
  if (!ctx) return
  const osz = ctx.createOscillator()
  const pegel = ctx.createGain()
  osz.type = 'sine'
  osz.frequency.value = hz

  const start = ctx.currentTime + ab
  pegel.gain.setValueAtTime(0, start)
  pegel.gain.linearRampToValueAtTime(laut, start + 0.04)
  pegel.gain.setValueAtTime(laut, start + dauer - 0.04)
  pegel.gain.linearRampToValueAtTime(0, start + dauer)

  osz.connect(pegel).connect(ctx.destination)
  osz.start(start)
  osz.stop(start + dauer)
  laufendeToene.push(osz)
  osz.onended = () => { laufendeToene = laufendeToene.filter((o) => o !== osz) }
}

function vibriere(muster: number[]): void {
  try {
    navigator.vibrate?.(muster)
  } catch {
    // Manche Browser werfen ohne vorherige Nutzerhandlung. Kein Grund, den
    // Ton mit in den Abgrund zu ziehen.
  }
}

/** Gemeinsamer Start: Kontext wecken, alten Ton abräumen, Takt setzen. */
function an(schlag: () => void, taktMs: number): void {
  if (takt) return
  if (!ctx) ctx = neuerKontext()
  if (ctx?.state === 'suspended') void ctx.resume().catch(() => undefined)
  try {
    schlag()
    takt = setInterval(schlag, taktMs)
  } catch {
    aus()
  }
}

/** Beendet, was gerade läuft. Der Kontext bleibt — er wird wieder gebraucht. */
export function aus(): void {
  if (takt) { clearInterval(takt); takt = null }
  for (const o of laufendeToene) {
    try { o.stop() } catch { /* lief schon aus */ }
  }
  laufendeToene = []
  vibriere(0 as unknown as number[])
}

/**
 * Anruf: der deutsche Rufton. 425 Hz, eine Sekunde an, drei Sekunden Takt.
 */
export function klingelnAn(): void {
  an(() => {
    ton(425, 0, 1, 0.25)
    vibriere([1000, 2000])
  }, 3000)
}

/**
 * SOS: kein Klingeln, sondern ein Alarm.
 *
 * Zwei wechselnde Töne im schnellen Takt, deutlich lauter, und ein hartes
 * Vibrationsmuster. Er soll sich hörbar von einem Anruf unterscheiden —
 * wer ihn hört, soll nicht erst auf den Bildschirm sehen müssen, um zu
 * wissen, dass es kein Anruf ist.
 *
 * Er hört von selbst nicht auf. Das ist Absicht: Ein Alarm, der nach zwei
 * Tönen verstummt, ist ein Alarm, den man verschläft.
 */
export function alarmAn(): void {
  an(() => {
    ton(880, 0, 0.35, 0.5)
    ton(660, 0.4, 0.35, 0.5)
    vibriere([400, 150, 400, 150, 400, 500])
  }, 1600)
}

/** Nur für Tests. */
export function laeuft(): boolean {
  return takt !== null
}
